import { useState } from "react";
import { Link } from "react-router-dom";

const SITE_URL = import.meta.env.VITE_SITE_URL || '';

const allReviews = [
  {
    stars: 5,
    headline: "Best CPR Class I've Ever Taken",
    text: "I trained with Destiny, and you can tell she's a real RN. She explained everything in a way that actually made sense in real-life situations. The hands-on practice was top-tier and I left feeling genuinely confident. Same-day certification made it even better.",
    name: "Maria G.",
    role: "Nursing Student",
    course: "BLS Certification",
    instructor: "Destiny",
    avatar: "https://public.readdy.ai/ai/img_res/9ffaada835f523ae9f0dbf4d3fe5dca5.jpg",
  },
  {
    stars: 5,
    headline: "Professional and Straight to the Point",
    text: "I had my class with Nathaniel, and he was extremely knowledgeable and patient. He made sure everyone understood proper technique and corrected us when needed. You can tell he actually works in the field — not just teaching from a slideshow.",
    name: "James T.",
    role: "EMT / First Responder",
    course: "BLS Certification",
    instructor: "Nathaniel",
    avatar: "https://readdy.ai/api/search-image?query=natural%20candid%20portrait%20Black%20man%20early%20thirties%20confident%20calm%20expression%20short%20fade%20haircut%20light%20beard%20casual%20button%20shirt%20soft%20neutral%20indoor%20background%20realistic%20photography&width=80&height=80&seq=rev-james-v3&orientation=squarish",
  },
  {
    stars: 5,
    headline: "Highly Recommend Cardiocraft CPR",
    text: "Destiny made the class engaging and easy to follow. I've taken CPR before, but this was the first time I actually felt confident doing compressions correctly. She brings real clinical context to everything she teaches. Definitely coming back for my renewal.",
    name: "Ashley R.",
    role: "ICU Nurse",
    course: "ACLS Certification",
    instructor: "Destiny",
    avatar: "https://readdy.ai/api/search-image?query=natural%20candid%20portrait%20Black%20woman%20late%20twenties%20professional%20confident%20natural%20curly%20hair%20scrubs%20or%20professional%20top%20soft%20warm%20indoor%20lighting%20realistic%20photography%20healthcare%20worker&width=80&height=80&seq=rev-ashley-v3&orientation=squarish",
  },
  {
    stars: 5,
    headline: "Hands-On and Very Real",
    text: "Training with Nathaniel was a great experience. He broke everything down step by step and made sure we practiced until we got it right. It didn't feel rushed at all. Our whole team walked out certified and actually prepared.",
    name: "David L.",
    role: "Restaurant Manager",
    course: "Group CPR/AED Training",
    instructor: "Nathaniel",
    avatar: "https://readdy.ai/api/search-image?query=natural%20candid%20portrait%20Hispanic%20man%20mid%20forties%20friendly%20relaxed%20expression%20dark%20short%20hair%20casual%20polo%20shirt%20soft%20neutral%20office%20background%20realistic%20photography&width=80&height=80&seq=rev-david-v3&orientation=squarish",
  },
  {
    stars: 5,
    headline: "Worth Every Dollar",
    text: "Destiny is amazing. She teaches from real experience, not just a book. The class was smooth, professional, and extremely informative. As a pediatric RN I've sat through a lot of certifications — this one stood out. Same-day cert was a huge plus for my schedule.",
    name: "Priya M.",
    role: "Pediatric RN",
    course: "PALS Certification",
    instructor: "Destiny",
    avatar: "https://readdy.ai/api/search-image?query=natural%20candid%20portrait%20South%20Asian%20Indian%20woman%20thirties%20professional%20warm%20smile%20dark%20hair%20pulled%20back%20medical%20scrubs%20soft%20clinical%20background%20realistic%20portrait%20photography&width=80&height=80&seq=rev-priya-v3&orientation=squarish",
  },
  {
    stars: 5,
    headline: "I Feel Prepared Now",
    text: "I was nervous going in but Nathaniel made it simple and practical. He walked us through every step clearly and the hands-on training with the mannequins helped a lot. I feel ready to actually respond in an emergency now. Would recommend to any teacher.",
    name: "Sandra K.",
    role: "Elementary School Teacher",
    course: "CPR / AED",
    instructor: "Nathaniel",
    avatar: "https://readdy.ai/api/search-image?query=natural%20candid%20portrait%20Hispanic%20woman%20mid%20forties%20warm%20kind%20expression%20dark%20shoulder%20length%20hair%20casual%20blazer%20or%20cardigan%20soft%20indoor%20background%20realistic%20photography%20teacher%20professional&width=80&height=80&seq=rev-sandra-v3&orientation=squarish",
  },
];

export default function ReviewsPage() {
  const [filter, setFilter] = useState("All");
  const tags = ["All", "BLS Certification", "ACLS Certification", "PALS Certification", "CPR / AED", "Group CPR/AED Training"];

  const filtered = filter === "All" ? allReviews : allReviews.filter((r) => r.course === filter);

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: 'Cardiocraft CPR',
    url: `${SITE_URL}/reviews`,
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '5',
      reviewCount: String(allReviews.length),
      bestRating: '5',
      worstRating: '1',
    },
    review: allReviews.map((r) => ({
      '@type': 'Review',
      author: { '@type': 'Person', name: r.name },
      reviewRating: { '@type': 'Rating', ratingValue: String(r.stars), bestRating: '5' },
      reviewBody: r.text,
      name: r.headline,
    })),
  };

  return (
    <main className="pt-20">
      <title>CPR Class Reviews Kissimmee | Student Testimonials | Cardiocraft CPR</title>
      <meta name="description" content="Read real student reviews of Cardiocraft CPR in Kissimmee, FL. See what nurses, EMTs, teachers, and community members say about RN-led BLS, ACLS, PALS, and CPR/AED training." />
      <meta name="keywords" content="Cardiocraft CPR reviews, CPR class testimonials Kissimmee, BLS certification reviews, ACLS PALS student feedback Florida" />
      <link rel="canonical" href={`${SITE_URL}/reviews`} />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
      />
      {/* Hero */}
      <section className="py-20 bg-brand-purple">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <span className="inline-block border border-brand-gold/50 text-brand-gold text-xs font-bold uppercase tracking-widest px-4 py-2 rounded-full mb-6">
            Student Testimonials
          </span>
          <h1 className="font-serif text-5xl lg:text-6xl font-bold text-white mb-4">
            What Our Students Say
          </h1>
          <p className="text-white/80 text-lg">
            Hundreds of certified professionals. Real stories from real students trained by Registered Nurses in Kissimmee.
          </p>
          <div className="flex justify-center gap-6 mt-8">
            {[{ val: "200+", label: "5-Star Reviews" }, { val: "100%", label: "Satisfied Students" }, { val: "1,500+", label: "Certifications Issued" }].map((s) => (
              <div key={s.label} className="text-center">
                <p className="font-serif text-3xl font-bold text-brand-gold">{s.val}</p>
                <p className="text-white/60 text-xs mt-1">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Reviews */}
      <section className="py-20 bg-brand-purple-pale">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          {/* Filter */}
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            {tags.map((tag) => (
              <button
                key={tag}
                onClick={() => setFilter(tag)}
                className={`px-4 py-2 text-sm font-semibold rounded-full border-2 transition-all cursor-pointer whitespace-nowrap ${
                  filter === tag
                    ? "bg-brand-purple border-brand-purple text-white"
                    : "bg-white border-gray-200 text-gray-600 hover:border-brand-purple/50"
                }`}
              >
                {tag}
              </button>
            ))}
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((review) => (
              <div
                key={review.name}
                className="bg-white rounded-xl p-8 border border-gray-100 hover:border-brand-purple/20 transition-all flex flex-col"
              >
                {/* Stars */}
                <div className="flex gap-1 mb-3">
                  {Array.from({ length: review.stars }).map((_, i) => (
                    <i key={i} className="ri-star-fill text-brand-gold text-lg" />
                  ))}
                </div>

                {/* Tags row */}
                <div className="flex flex-wrap gap-2 mb-4">
                  <span className="inline-flex items-center gap-1 bg-brand-purple-pale text-brand-purple text-xs font-semibold px-2.5 py-1 rounded-full">
                    {review.course}
                  </span>
                  <span
                    className="inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full whitespace-nowrap"
                    style={{
                      background: "linear-gradient(135deg, rgba(107,70,193,0.12), rgba(212,175,55,0.08))",
                      border: "1px solid rgba(212,175,55,0.4)",
                      color: "#7c4ec4",
                    }}
                  >
                    <i className="ri-nurse-line text-xs" style={{ color: "#c9a227" }} />
                    Trained with {review.instructor}, RN
                  </span>
                </div>

                {/* Headline */}
                <h3 className="font-semibold text-brand-black text-base mb-3">{review.headline}</h3>

                {/* Review text */}
                <p className="text-gray-500 text-sm leading-relaxed mb-6 flex-1">{review.text}</p>

                {/* Reviewer */}
                <div className="flex items-center gap-3 pt-4 border-t border-gray-100">
                  <img
                    src={review.avatar}
                    alt={review.name}
                    className="w-12 h-12 rounded-full object-cover object-top border-2 border-brand-gold shrink-0"
                  />
                  <div>
                    <p className="font-semibold text-brand-black text-sm">{review.name}</p>
                    <p className="text-gray-400 text-xs">{review.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-white border-t border-gray-100">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-serif text-3xl font-bold text-brand-black mb-4">
            Join Our Growing Community of Certified Professionals
          </h2>
          <p className="text-gray-500 mb-8">
            Experience the Cardiocraft difference — RN-led instruction, same-day certification, Kissimmee, FL.
          </p>
          <Link
            to="/booking#reserve-spot"
            className="inline-flex items-center gap-2 bg-brand-gold text-brand-black font-bold px-8 py-4 rounded-md hover:bg-brand-gold-dark transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-calendar-check-line" />
            Book Your Class Now
          </Link>
        </div>
      </section>
    </main>
  );
}
